import { useState, type ReactNode } from 'react';
import type { CurrencyCode } from '@nest-recruit/shared';
import { Field } from '../../../components/Field';
import { MoneyInput } from '../../../components/MoneyInput';
import { Segmented } from '../../../components/Segmented';
import { SettingFact, SettingRow } from '../../../components/SettingRow';
import { ToneChip } from '../../../components/Tone';
import { useToasts } from '../../../stores/toasts';
import { BUDGET_MODE_LABELS, budgetModeOf, checkBudget, tryItResult, type BudgetMode } from './basics-tabs';
import styles from './BasicsStep.module.css';

/**
 * Basics' Salary budget tab (Job-salary-budget mockup; owner 2026-10-18 Q2, 2026-10-19 Q1): what
 * the job can pay, a month, as a range, only a top figure, or none. The mode is read from the
 * figures and never stored; picking "Only a top figure" drops the from, and "No budget" clears
 * both with ten seconds to take it back (CLAUDE.md §5a rule 8). Under it, how the over-budget mark
 * works, and a box to try it.
 */

export interface BudgetFigures {
  /** Whole units as typed, digits only; "" when empty. */
  readonly min: string;
  readonly max: string;
}

const PER_MONTH = 'a month';
const BUDGET_MODES: readonly BudgetMode[] = ['range', 'top', 'none'];

interface SalaryBudgetPanelProps {
  readonly figures: BudgetFigures;
  readonly onChange: (next: BudgetFigures) => void;
  readonly currency: CurrencyCode;
  readonly isReadOnly: boolean;
  /** The position's band, read only, when the job was made from one (2026-10-19 Q1 b). */
  readonly band?: ReactNode;
}

export function SalaryBudgetPanel({ figures, onChange, currency, isReadOnly, band }: SalaryBudgetPanelProps): ReactNode {
  const showUndo = useToasts((toasts) => toasts.showUndo);
  // A pick shows its boxes before anything is typed in them; otherwise the figures decide.
  const [pickedMode, setPickedMode] = useState<BudgetMode | null>(null);
  // The "Try it" box's own figure: never saved, only there to show what the mark would say.
  const [asks, setAsks] = useState('');
  const mode = pickedMode ?? budgetModeOf(figures.min, figures.max);
  const budget = checkBudget(figures.min, figures.max);
  const tryIt = tryItResult(asks, mode === 'none' ? null : budget.max, currency);

  function pick(next: BudgetMode): void {
    setPickedMode(next);
    if (next === 'range') return;
    if (next === 'top') {
      if (figures.min !== '') onChange({ min: '', max: figures.max });
      return;
    }
    if (figures.min === '' && figures.max === '') return;
    const before = figures;
    onChange({ min: '', max: '' });
    showUndo('Budget cleared', () => {
      setPickedMode(null);
      onChange(before);
    });
  }

  return (
    <>
      <SettingRow label="Budget" isOptional isGroup hint="Monthly, gross. Hidden from anyone who may not see salaries.">
        {() => (
          <Segmented
            label="Budget"
            disabled={isReadOnly}
            value={mode}
            options={BUDGET_MODES.map((option) => ({ value: option, label: BUDGET_MODE_LABELS[option] }))}
            onChange={pick}
          />
        )}
      </SettingRow>
      {mode === 'range' && (
        <SettingRow label="From – to" isGroup hint="Only the top counts for the mark." error={budget.message ?? undefined}>
          {({ describedBy }) => (
            <>
              <div className={styles.money}>
                <MoneyInput label="From" describedBy={describedBy} currency={currency} suffix={PER_MONTH} isInvalid={budget.isMinBad} disabled={isReadOnly} value={figures.min} onChange={(min) => onChange({ ...figures, min })} />
              </div>
              <span className={styles.between}>to</span>
              <div className={styles.money}>
                <MoneyInput label="To, the top" describedBy={describedBy} currency={currency} suffix={PER_MONTH} isInvalid={budget.isMaxBad} disabled={isReadOnly} value={figures.max} onChange={(max) => onChange({ ...figures, max })} />
              </div>
            </>
          )}
        </SettingRow>
      )}
      {mode === 'top' && (
        <SettingRow label="Up to" hint="Anyone asking for more than this is marked." error={budget.message ?? undefined}>
          {({ inputId, describedBy }) => (
            <div className={styles.money}>
              <MoneyInput id={inputId} describedBy={describedBy} currency={currency} suffix={PER_MONTH} isInvalid={budget.isMaxBad} disabled={isReadOnly} value={figures.max} onChange={(max) => onChange({ min: '', max })} />
            </div>
          )}
        </SettingRow>
      )}
      {mode === 'none' && <SettingFact label="No budget">Expected salaries show as they are, with no mark.</SettingFact>}
      {band !== undefined && (
        <SettingFact label="The position's band" hint="Where the budget started.">
          {band}
        </SettingFact>
      )}
      <section className={styles.how} aria-label="How the mark works">
        <h3 className={styles.howTitle}>How the mark works</h3>
        <p className={styles.howText}>
          Anyone asking for more than the top is marked red, with how much more. A warning only: no score changes.
        </p>
        <div className={styles.tryRow}>
          <div className={styles.tryField}>
            <Field label="Try it: someone asks">{({ inputId }) => <MoneyInput id={inputId} currency={currency} suffix={PER_MONTH} value={asks} onChange={setAsks} />}</Field>
          </div>
          <div className={styles.result} data-over={tryIt.isOver || undefined} aria-live="polite">
            <span className={styles.amount}>{tryIt.asksText}</span>
            {tryIt.isOver && <ToneChip tone="urgent">{tryIt.overLabel}</ToneChip>}
            <span>{tryIt.note}</span>
          </div>
        </div>
      </section>
    </>
  );
}
